import { useCallback, useMemo, useState } from 'react'
import { SkeletonList } from '../componentes/Skeleton'
import { EstadoError } from '../componentes/EstadoError'
import { EstadoVacio } from '../componentes/EstadoVacio'
import { useFiltrosAgendamiento, type Modo } from '../hooks/useFiltrosAgendamiento'
import { BarraPublicar } from './agendamiento/BarraPublicar'
import { BarraSeleccion } from './agendamiento/BarraSeleccion'
import { bonosDelCatalogo } from './agendamiento/catalogo'
import {
  TAMANO_PAGINA,
  useCatalogo,
  useHistoriales,
  usePublicar,
  useResolver,
  useTablaClientes,
} from './agendamiento/consultas'
import { DetallePedido } from './agendamiento/DetallePedido'
import type {
  FichasElegidas,
  FilaAgendamiento,
  LineasAnadidas,
  PreciosEditados,
} from './agendamiento/filas'
import {
  aEntradaPublicar,
  clavePrecio,
  lineasDe,
  motivoNoPublicable,
  partnerElegido,
  publicables,
} from './agendamiento/filas'
import { FiltrosTabla } from './agendamiento/FiltrosTabla'
import { ModoIndividual } from './agendamiento/ModoIndividual'
import { PanelReparto, type ExtraElegido } from './agendamiento/PanelReparto'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { ModoMasivo } from './agendamiento/ModoMasivo'
import { PanelResultados, REINTENTABLES } from './agendamiento/PanelResultados'
import { FichaResuelta, SelectorFicha } from './agendamiento/SelectorFicha'
import type { FilaTabla } from './agendamiento/tabla'
import {
  DIAS,
  SEMANAS,
  aEntradaResolver,
  bonoDelHabitual,
  filasDeTabla,
  sinBono,
} from './agendamiento/tabla'
import { TablaClientes } from './agendamiento/TablaClientes'
import { TablaResolucion } from './agendamiento/TablaResolucion'

/**
 * Agendar repartos. Sustituye a la herramienta `Agendamiento masivo admin` de
 * n8n: al publicar, el backend crea el presupuesto en Odoo en borrador y
 * dispara el webhook que crea el evento de calendario.
 *
 * **La tabla es la pantalla.** El trabajo de un dia cualquiera es "quien reparte
 * el martes": se elige el dia, salen sus clientes con su pedido de siempre ya
 * puesto, se marcan los que van y se publica. Antes eso empezaba en un cuadro de
 * texto donde habia que pegar una lista sacada de otro sitio, que es pedirle al
 * administrador que haga a mano la consulta que la base de datos sabe hacer.
 *
 * Pegar la lista **no se ha quitado**: funciona, hay quien trabaja asi, y sigue
 * ahi detras de un enlace. Lo mismo el alta suelta, que es el unico camino que
 * admite extras -dispensador, fianza, portes-.
 *
 * Los tres caminos se juntan en la revision: de ahi en adelante comparten la
 * eleccion de ficha, la comparacion de precios y el boton de publicar. Duplicar
 * ese tramo habria significado tres sitios donde olvidarse de comprobar que la
 * ficha esta elegida, que es la comprobacion que evita mandar un pedido a otro
 * cliente.
 *
 * El estado compartible -dia de ruta, semana, busqueda, fecha- vive en la url.
 * Las casillas marcadas no: un enlace que reconstruyera treinta clientes listos
 * para publicar seria un enlace peligroso.
 */

const CLASE_ENLACE =
  'rounded-lg px-2 py-1 text-sm font-medium text-acento-tinta underline underline-offset-2 transition-colors hover:bg-elevado'

const CLASE_PAGINACION =
  'rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:border-borde disabled:text-desactivado-tinta'

export function Agendamiento() {
  const {
    filtros,
    cambiarModo,
    cambiarFecha,
    cambiarBusqueda,
    elegirCliente,
    cambiarRuta,
    cambiarSemana,
    irAPagina,
  } = useFiltrosAgendamiento()

  const [filas, setFilas] = useState<FilaAgendamiento[]>([])
  const [fichas, setFichas] = useState<FichasElegidas>({})
  const [precios, setPrecios] = useState<PreciosEditados>({})
  // Los productos que el administrador le suma al pedido de un cliente
  // concreto, ya en la revision. Es lo que permite agendar a diez clientes su
  // bono de siempre y a uno de ellos, ademas, un dispensador.
  const [anadidas, setAnadidas] = useState<LineasAnadidas>({})
  const [seleccionada, setSeleccionada] = useState<string | null>(null)

  // Lo marcado se guarda con la fila entera y no solo con el id: asi cambiar de
  // pagina o de dia no pierde lo que ya estaba marcado, y la revision sabe el
  // nombre y el NIF de cada uno aunque su fila ya no este a la vista.
  const [marcados, setMarcados] = useState<Map<string, FilaTabla>>(() => new Map())
  const [bonosPorFila, setBonosPorFila] = useState<Record<string, string>>({})
  // Rediseño B.3: fila abierta en el panel lateral, extras (packs/sueltos) y día
  // de reparto por cliente. Van por contact_id para sobrevivir a la repaginación.
  const [filaAbierta, setFilaAbierta] = useState<string | null>(null)
  const [extrasPorCliente, setExtrasPorCliente] = useState<Record<string, ExtraElegido[]>>({})
  const [diaPorCliente, setDiaPorCliente] = useState<Record<string, DiaRuta | null>>({})

  const enTabla = filtros.modo === 'tabla'

  const tabla = useTablaClientes(
    { ruta: filtros.ruta, semana: filtros.semana, q: filtros.q, page: filtros.page },
    enTabla,
  )
  const catalogo = useCatalogo(enTabla)
  const resolverTabla = useResolver()
  const publicar = usePublicar()

  // Una revision nueva borra las decisiones de la anterior: una ficha elegida
  // para el cliente de la fila 3 no significa nada en otra lista.
  const reiniciar = useCallback(
    (nuevas: FilaAgendamiento[]) => {
      setFilas(nuevas)
      setFichas({})
      setPrecios({})
      setAnadidas({})
      setSeleccionada(nuevas.length === 1 ? nuevas[0].clave : null)
      publicar.reset()
    },
    [publicar],
  )

  const contactIds = useMemo(
    () =>
      [
        ...new Set(
          filas
            .map((fila) => fila.resultado?.cliente?.contact_id)
            .filter((id): id is string => Boolean(id)),
        ),
      ].sort(),
    [filas],
  )
  const historiales = useHistoriales(contactIds)

  const listas = publicables(filas, fichas, anadidas)
  const detalle = filas.find((fila) => fila.clave === seleccionada) ?? null

  // Estables: son dependencias de las columnas memoizadas de la tabla, y una
  // funcion nueva por render las reconstruiria en cada pulsacion.
  const seleccionar = useCallback(
    (clave: string) => setSeleccionada((previa) => (previa === clave ? null : clave)),
    [],
  )

  const elegirFicha = useCallback((clave: string, id: number | null) => {
    setFichas((previas) => {
      if (id === null) {
        const { [clave]: _descartada, ...resto } = previas
        return resto
      }
      return { ...previas, [clave]: id }
    })
  }, [])

  // --- La tabla de clientes -------------------------------------------------

  const productos = useMemo(() => catalogo.data?.items ?? [], [catalogo.data])
  const bonos = useMemo(() => bonosDelCatalogo(productos), [productos])
  const filasTabla = useMemo(() => tabla.data?.items ?? [], [tabla.data])
  const clavesMarcadas = useMemo(() => new Set(marcados.keys()), [marcados])

  /**
   * El bono de una fila: el que haya elegido el administrador manda sobre el que
   * dedujo el backend del ultimo pedido. Una cadena vacia guardada es una
   * decision -"ninguno"- y por eso se distingue de "no ha tocado nada".
   */
  const bonoDeFila = useCallback(
    (fila: FilaTabla) => {
      const elegido = bonosPorFila[fila.cliente.contact_id]
      if (elegido !== undefined) {
        return bonos.find((bono) => String(bono.id) === elegido) ?? null
      }
      return bonoDelHabitual(bonos, fila.pedido_habitual)
    },
    [bonos, bonosPorFila],
  )

  const alternarMarca = useCallback((fila: FilaTabla) => {
    setMarcados((previos) => {
      const siguientes = new Map(previos)
      if (siguientes.has(fila.cliente.contact_id)) siguientes.delete(fila.cliente.contact_id)
      else siguientes.set(fila.cliente.contact_id, fila)
      return siguientes
    })
  }, [])

  /** Solo los de la pagina que se esta viendo: "todos" no puede alcanzar a los que el filtro dejo fuera. */
  const marcarVisibles = useCallback(
    (marcar: boolean) => {
      setMarcados((previos) => {
        const siguientes = new Map(previos)
        for (const fila of filasTabla) {
          if (marcar) siguientes.set(fila.cliente.contact_id, fila)
          else siguientes.delete(fila.cliente.contact_id)
        }
        return siguientes
      })
    },
    [filasTabla],
  )

  const elegirBono = useCallback((contactId: string, productoId: string) => {
    setBonosPorFila((previos) => ({ ...previos, [contactId]: productoId }))
  }, [])

  // Rediseño B.3: abrir/cerrar el panel lateral y guardar extras/día por cliente.
  const abrirFila = useCallback(
    (fila: FilaTabla) =>
      setFilaAbierta((previa) =>
        previa === fila.cliente.contact_id ? null : fila.cliente.contact_id,
      ),
    [],
  )
  const cambiarExtras = useCallback(
    (contactId: string, extras: ExtraElegido[]) =>
      setExtrasPorCliente((previos) => ({ ...previos, [contactId]: extras })),
    [],
  )
  const elegirDia = useCallback(
    (contactId: string, dia: DiaRuta | null) =>
      setDiaPorCliente((previos) => ({ ...previos, [contactId]: dia })),
    [],
  )

  const soltarMarcados = () => {
    setMarcados(new Map())
    setFilaAbierta(null)
    setExtrasPorCliente({})
    setDiaPorCliente({})
    reiniciar([])
  }

  const marcadas = [...marcados.values()]
  const faltanBono = marcadas.filter((fila) => sinBono(bonoDeFila(fila))).length

  const revisarMarcados = () => {
    if (marcadas.length === 0) return
    resolverTabla.mutate(
      { clientes: marcadas.map((fila) => aEntradaResolver(fila, bonoDeFila(fila))) },
      {
        onSuccess: (respuesta) => {
          const nuevas = filasDeTabla(marcadas, respuesta.resultados)
          reiniciar(nuevas)
          // Siembra las líneas añadidas con los extras (packs/sueltos) que el
          // administrador eligió en el panel lateral de cada cliente. La clave
          // de fila en la tabla es el contact_id.
          const sembradas: LineasAnadidas = {}
          for (const fila of nuevas) {
            const extras = extrasPorCliente[fila.clave]
            if (extras && extras.length > 0) {
              sembradas[fila.clave] = extras.map((e) => ({
                nombre: e.nombre,
                cantidad: e.cantidad,
                precio: e.precio,
                product_id: e.productoId,
              }))
            }
          }
          if (Object.keys(sembradas).length > 0) setAnadidas(sembradas)
        },
      },
    )
  }

  const irAModo = (modo: Modo) => {
    cambiarModo(modo)
    setMarcados(new Map())
    setBonosPorFila({})
    setFilaAbierta(null)
    setExtrasPorCliente({})
    setDiaPorCliente({})
    reiniciar([])
  }

  // --- Publicar -------------------------------------------------------------

  /**
   * Fecha ISO del día de ruta (r1..r5) elegido para un cliente, situado en la
   * misma semana que la fecha global de la tanda. Así el albarán de ese cliente
   * sale el día que se le asignó (B.3), no la fecha común. Sin día propio → global.
   */
  const fechaDelCliente = (contactId: string): string | undefined => {
    const dia = diaPorCliente[contactId]
    if (!dia || filtros.fecha === '') return undefined
    const offset: Record<DiaRuta, number> = { r1: 1, r2: 2, r3: 3, r4: 4, r5: 5 }
    const base = new Date(`${filtros.fecha}T00:00:00`)
    if (Number.isNaN(base.getTime())) return undefined
    // getDay(): 0=domingo..6=sábado. Se mueve al día de ruta de esa semana.
    const lunes = new Date(base)
    lunes.setDate(base.getDate() - ((base.getDay() + 6) % 7)) // lunes de la semana
    const objetivo = new Date(lunes)
    objetivo.setDate(lunes.getDate() + (offset[dia] - 1))
    const y = objetivo.getFullYear()
    const m = String(objetivo.getMonth() + 1).padStart(2, '0')
    const d = String(objetivo.getDate()).padStart(2, '0')
    const iso = `${y}-${m}-${d}`
    return iso === filtros.fecha ? undefined : iso // si coincide con la global, se omite
  }

  const publicarFilas = (aPublicar: FilaAgendamiento[]) => {
    const clientes = aPublicar
      .map((fila) =>
        aEntradaPublicar(fila, fichas, precios, anadidas, fechaDelCliente(fila.clave)),
      )
      .filter((entrada) => entrada !== null)
    if (clientes.length === 0 || filtros.fecha === '') return
    publicar.mutate({ fecha_reparto: filtros.fecha, clientes })
  }

  const reintentar = () => {
    const pendientes = new Set(
      (publicar.data?.resultados ?? [])
        .filter((resultado) => REINTENTABLES.has(resultado.estado))
        .map((resultado) => resultado.contact_id),
    )
    publicarFilas(
      listas.filter((fila) => pendientes.has(fila.resultado?.cliente?.contact_id ?? '')),
    )
  }

  const total = tabla.data?.total ?? 0
  const tamano = tabla.data?.page_size || TAMANO_PAGINA
  const pagina = tabla.data?.page ?? filtros.page
  const paginas = Math.max(1, Math.ceil(total / tamano))

  // Los filtros activos, escritos como se leen en los botones: "el viernes" y
  // no "r5". El codigo es del backend y quien mira la pantalla no lo conoce.
  const activos: string[] = []
  if (filtros.q) activos.push(`la busqueda "${filtros.q}"`)
  if (filtros.ruta) {
    activos.push(`el ${DIAS.find((dia) => dia.clave === filtros.ruta)?.etiqueta.toLowerCase()}`)
  }
  if (filtros.semana) {
    activos.push(
      `la ${SEMANAS.find((semana) => semana.clave === filtros.semana)?.etiqueta.toLowerCase()}`,
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-xl font-semibold text-tinta">Agendamiento</h1>
        <p className="mt-1 text-sm text-tinta-apagada">
          Crea los presupuestos del reparto en Odoo y avisa a n8n para que ponga el evento en el
          calendario. Los presupuestos se crean en borrador: aprobarlos sigue siendo manual.
        </p>
      </div>

      {/* La fecha en modo tabla vive en la barra de filtros (rediseño A). En los
          otros modos (individual/masivo), que no tienen esa barra, sigue aquí. */}
      {!enTabla ? (
        <div>
          <label htmlFor="fecha-reparto" className="block text-sm font-medium text-tinta-2">
            Fecha de reparto
          </label>
          <input
            id="fecha-reparto"
            type="date"
            value={filtros.fecha}
            onChange={(evento) => cambiarFecha(evento.target.value)}
            className="mt-1 rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo [color-scheme:dark]"
          />
          <p className="mt-1 text-xs text-tinta-apagada">
            El pedido se crea a las 08:00 de ese dia, que es por lo que filtra el reparto.
          </p>
        </div>
      ) : null}

      {enTabla ? (
        <section aria-label="Clientes del reparto" className="flex min-w-0 flex-col gap-4">
          <FiltrosTabla
            filtros={filtros}
            fecha={filtros.fecha}
            onFecha={cambiarFecha}
            onRuta={cambiarRuta}
            onSemana={cambiarSemana}
            onBuscar={cambiarBusqueda}
          />

          {tabla.isPending ? (
            // Una pagina de 100 clientes tarda unos tres segundos: el skeleton
            // muestra la silueta de la tabla para que no parezca colgada.
            <SkeletonList />
          ) : tabla.isError ? (
            <EstadoError error={tabla.error} onReintentar={() => void tabla.refetch()} />
          ) : filasTabla.length === 0 ? (
            <EstadoVacio
              titulo="Ningun cliente con estos filtros"
              descripcion={
                activos.length > 0
                  ? `No hay ningun cliente que case con ${activos.join(', ')}.`
                  : 'El listado ha llegado vacio de la API.'
              }
            />
          ) : (
            <>
              {tabla.isFetching ? (
                <p role="status" aria-live="polite" className="text-sm text-tinta-apagada">
                  Actualizando la tabla
                </p>
              ) : null}

              <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
                <div className="min-w-0">
                  <TablaClientes
                    filas={filasTabla}
                    bonos={bonos}
                    catalogoPendiente={catalogo.isPending}
                    marcados={clavesMarcadas}
                    bonoDeFila={bonoDeFila}
                    onAlternar={alternarMarca}
                    onMarcarVisibles={marcarVisibles}
                    onElegirBono={elegirBono}
                    onAbrirFila={abrirFila}
                    filaAbierta={filaAbierta}
                  />
                </div>

                {/* Panel lateral B.3: se abre al pulsar un cliente. */}
                {(() => {
                  const abierta = filasTabla.find((f) => f.cliente.contact_id === filaAbierta)
                  return abierta ? (
                    <PanelReparto
                      fila={abierta}
                      catalogo={productos}
                      catalogoPendiente={catalogo.isPending}
                      bonoElegido={bonoDeFila(abierta)}
                      extras={extrasPorCliente[abierta.cliente.contact_id] ?? []}
                      dia={diaPorCliente[abierta.cliente.contact_id] ?? null}
                      onElegirBono={elegirBono}
                      onCambiarExtras={cambiarExtras}
                      onElegirDia={elegirDia}
                      onCerrar={() => setFilaAbierta(null)}
                    />
                  ) : (
                    <aside className="hidden rounded-xl border border-dashed border-borde p-4 text-sm text-tinta-apagada lg:block">
                      Pulsa un cliente para elegir su bono, packs, botellones sueltos y el día de reparto.
                    </aside>
                  )
                })()}
              </div>

              <nav
                aria-label="Paginacion"
                className="flex items-center justify-between gap-3 text-sm"
              >
                <button
                  type="button"
                  aria-label="Pagina anterior"
                  disabled={pagina <= 1}
                  onClick={() => irAPagina(pagina - 1)}
                  className={CLASE_PAGINACION}
                >
                  Anterior
                </button>
                <span className="text-tinta-apagada">
                  Pagina {pagina} de {paginas}
                </span>
                <button
                  type="button"
                  aria-label="Pagina siguiente"
                  disabled={pagina >= paginas}
                  onClick={() => irAPagina(pagina + 1)}
                  className={CLASE_PAGINACION}
                >
                  Siguiente
                </button>
              </nav>
            </>
          )}

          <BarraSeleccion
            marcados={marcadas.length}
            sinBono={faltanBono}
            fecha={filtros.fecha}
            revisando={resolverTabla.isPending}
            onRevisar={revisarMarcados}
            onSoltar={soltarMarcados}
          />

          {resolverTabla.isError ? <EstadoError error={resolverTabla.error} /> : null}
        </section>
      ) : null}

      {/* Los otros dos caminos, discretos: la lista pegada para quien la tiene
          en Excel y el alta suelta para quien necesita extras. */}
      <div className="flex flex-wrap items-center gap-2 text-sm text-tinta-apagada">
        {enTabla ? (
          <>
            <span>Otras formas de agendar:</span>
            <button type="button" onClick={() => irAModo('masivo')} className={CLASE_ENLACE}>
              Tengo la lista en Excel
            </button>
            <button type="button" onClick={() => irAModo('individual')} className={CLASE_ENLACE}>
              Alta suelta con extras
            </button>
          </>
        ) : (
          <button type="button" onClick={() => irAModo('tabla')} className={CLASE_ENLACE}>
            Volver a la tabla de clientes
          </button>
        )}
      </div>

      {filtros.modo === 'individual' ? (
        <ModoIndividual
          q={filtros.q}
          cliente={filtros.cliente}
          onBuscar={cambiarBusqueda}
          onElegirCliente={(id) => {
            elegirCliente(id)
            reiniciar([])
          }}
          onFilas={reiniciar}
        />
      ) : null}

      {filtros.modo === 'masivo' ? <ModoMasivo onFilas={reiniciar} /> : null}

      {filas.length > 0 ? (
        <section aria-label="Revision del pedido" className="flex min-w-0 flex-col gap-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-tinta-apagada">
            Revision
          </h2>

          {filtros.modo === 'individual' ? null : (
            <TablaResolucion
              filas={filas}
              fichas={fichas}
              precios={precios}
              anadidas={anadidas}
              historiales={historiales}
              seleccionada={seleccionada}
              onSeleccionar={seleccionar}
              onElegirFicha={elegirFicha}
              etiqueta={enTabla ? 'Revision del reparto' : 'Resolucion de la lista'}
            />
          )}

          {detalle ? (
            <div className="flex flex-col gap-3">
              {/* La ficha del modo individual se elige aqui: no hay tabla en la
                  que meter el desplegable, pero la regla es la misma y nunca se
                  adivina. */}
              {filtros.modo === 'individual' && detalle.resultado ? (
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-medium text-tinta-2">Ficha de Odoo</span>
                  {(detalle.resultado.candidatos ?? []).length > 0 ? (
                    <SelectorFicha
                      cliente={detalle.resultado.cliente?.nombre ?? detalle.nif}
                      candidatos={detalle.resultado.candidatos ?? []}
                      elegida={partnerElegido(detalle, fichas)}
                      onElegir={(id) => elegirFicha(detalle.clave, id)}
                    />
                  ) : detalle.resultado.ficha ? (
                    <FichaResuelta ficha={detalle.resultado.ficha} />
                  ) : (
                    <span className="text-sm text-tinta-apagada">
                      Este cliente no tiene ficha en Odoo.
                    </span>
                  )}
                </div>
              ) : null}

              {lineasDe(detalle, precios, anadidas).length > 0 || detalle.resultado?.cliente ? (
                <DetallePedido
                  lineas={lineasDe(detalle, precios, anadidas)}
                  catalogo={catalogo.data?.items}
                  anadidas={anadidas[detalle.clave] ?? []}
                  onCambiarAnadidas={(lineas) =>
                    setAnadidas((previas) => ({ ...previas, [detalle.clave]: lineas }))
                  }
                  historial={historiales.get(detalle.resultado?.cliente?.contact_id ?? '')}
                  onCambiarPrecio={(indice, precio) =>
                    setPrecios((previos) => {
                      const clave = clavePrecio(detalle.clave, indice)
                      if (precio === null) {
                        const { [clave]: _, ...resto } = previos
                        return resto
                      }
                      return { ...previos, [clave]: precio }
                    })
                  }
                />
              ) : (
                <p className="rounded-lg border border-critico-anillo bg-critico-fondo px-3 py-2 text-sm text-critico-tinta">
                  {motivoNoPublicable(detalle, fichas, anadidas) ?? 'Esta fila no tiene pedido.'}
                </p>
              )}
            </div>
          ) : null}

          <BarraPublicar
            clientes={listas.length}
            excluidos={filas.length - listas.length}
            fecha={filtros.fecha}
            publicando={publicar.isPending}
            onPublicar={() => publicarFilas(listas)}
          />
        </section>
      ) : null}

      {publicar.isError ? <EstadoError error={publicar.error} /> : null}

      {publicar.data ? (
        <PanelResultados
          respuesta={publicar.data}
          reintentando={publicar.isPending}
          onReintentar={reintentar}
        />
      ) : null}
    </div>
  )
}
